/**
 * API client.
 *
 * The session lives in an HttpOnly cookie, so the browser attaches it
 * automatically; the client only has to echo the CSRF cookie back in a header
 * for state-changing requests. Errors arrive as stable codes (never sentences),
 * which the UI translates — see `src/i18n`.
 */

import type {
  DailyReport,
  DataSource,
  DeviceSummary,
  Incident,
  LoginResult,
  Me,
  PlantSummary,
  Portfolio,
  MisPeriod,
  MisSummary,
  Resolution,
  Series,
} from './types';

const BASE = '/api/v1';
const CSRF_COOKIE = 'usolar_csrf';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params: Record<string, unknown>;

  constructor(status: number, code: string, params: Record<string, unknown> = {}) {
    super(code);
    this.status = status;
    this.code = code;
    this.params = params;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf && init.method && init.method !== 'GET') headers.set('X-CSRF-Token', csrf);

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const code = body?.code ?? `http.${response.status}`;
    throw new ApiError(response.status, code, body?.params ?? {});
  }
  return body as T;
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export const api = {
  // --- auth ---
  login: (email: string, password: string) =>
    request<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<Me>('/me'),
  verifyMfa: (code: string) =>
    request<Me>('/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  enrollMfa: () =>
    request<{ secret: string; otpauth_uri: string }>('/auth/mfa/enroll', { method: 'POST' }),
  confirmMfa: (code: string) =>
    request<{ recovery_codes: string[] }>('/auth/mfa/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  selectTenant: (tenantId: string) =>
    request<Me>('/auth/tenant', { method: 'POST', body: JSON.stringify({ tenant_id: tenantId }) }),

  // --- plants ---
  portfolio: () => request<Portfolio>('/portfolio'),
  plants: () => request<PlantSummary[]>('/plants'),
  plant: (plantId: string) => request<PlantSummary>(`/plants/${plantId}`),
  plantOverview: (plantId: string) =>
    request<import('./types').PlantOverview>(`/plants/${plantId}/overview`),
  devices: (plantId: string, kind?: string) =>
    request<DeviceSummary[]>(`/plants/${plantId}/devices${query({ kind })}`),

  // --- telemetry ---
  plantTelemetry: (
    plantId: string,
    options: {
      from?: string;
      to?: string;
      resolution?: Resolution;
      metrics?: string;
      /** Device kind to aggregate; irradiance lives on the sensor box. */
      kind?: string;
    },
  ) => request<Series>(`/plants/${plantId}/telemetry${query({ ...options })}`),
  deviceTelemetry: (
    deviceId: string,
    options: { from?: string; to?: string; resolution?: Resolution; metrics?: string },
  ) => request<Series>(`/devices/${deviceId}/telemetry${query({ ...options })}`),
  compare: (payload: {
    subjects: Array<{ type: 'device' | 'section' | 'plant'; id: string }>;
    from?: string;
    to?: string;
    resolution?: Resolution;
    metrics?: string[];
    normalize?: boolean;
  }) => request<Series[]>('/analytics/compare', { method: 'POST', body: JSON.stringify(payload) }),
  csvUrl: (deviceId: string, options: { from?: string; to?: string; resolution?: Resolution }) =>
    `${BASE}/devices/${deviceId}/telemetry.csv${query({ ...options })}`,

  // --- incidents ---
  incidents: (options: { plantId?: string; openOnly?: boolean; limit?: number } = {}) =>
    request<Incident[]>(
      `/incidents${query({ plantId: options.plantId, openOnly: options.openOnly, limit: options.limit })}`,
    ),
  acknowledgeIncident: (incidentId: string, note?: string) =>
    request<Incident>(`/incidents/${incidentId}/ack`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),
  closeIncident: (incidentId: string, resolution: string, rootCause?: string) =>
    request<Incident>(`/incidents/${incidentId}/close`, {
      method: 'POST',
      body: JSON.stringify({ resolution, root_cause: rootCause ?? null }),
    }),

  // --- MIS (management summary) ---
  misSummary: (options: { period?: MisPeriod; from?: string; to?: string; trendMonths?: number }) =>
    request<MisSummary>(
      `/mis/summary${query({
        period: options.period,
        from: options.from,
        to: options.to,
        trendMonths: options.trendMonths,
      })}`,
    ),
  misCsvUrl: (options: { period?: MisPeriod; from?: string; to?: string; trendMonths?: number }) =>
    `${BASE}/mis/summary${query({ ...options, format: 'csv' })}`,

  // --- reports & admin ---
  dailyReport: (plantId: string, date: string) =>
    request<DailyReport>(`/reports/daily${query({ plantId, date })}`),
  reportCsvUrl: (plantId: string, date: string) =>
    `${BASE}/reports/daily${query({ plantId, date, format: 'csv' })}`,
  dataSources: (plantId?: string) => request<DataSource[]>(`/admin/data-sources${query({ plantId })}`),
  probeDataSource: (sourceId: string) =>
    request<{ reachable: boolean; latency_ms: number | null; detail: string | null }>(
      `/admin/data-sources/${sourceId}/health`,
      { method: 'POST' },
    ),
  pollDataSource: (sourceId: string) =>
    request<{ status: string; devices_seen: number; samples_written: number; error: string | null }>(
      `/admin/data-sources/${sourceId}/poll`,
      { method: 'POST' },
    ),
  ingestRuns: (sourceId?: string) =>
    request<
      Array<{
        id: string;
        data_source_id: string;
        status: string;
        started_at: string;
        duration_ms: number | null;
        devices_seen: number;
        samples_written: number;
        stale_devices: number;
        error: string | null;
      }>
    >(`/admin/ingest-runs${query({ sourceId })}`),
};
